import { motion } from 'framer-motion';
import { LuvImage, UniversalLink } from '@/components';
import { TypeKeyValueListFields } from '@/core/generated-types';
import { generateTranslationsObject } from '@/utils/translations';
import { Asset, Entry } from 'contentful';
import React, { useState } from 'react';
import { UniversalLinkFields } from '@/components/common/UniversalLink';
import classNames from 'classnames';
import { homepageRedesignTranslations, ImagesWithCta } from '../types';

const ImageCard = ({
  image,
  item,
  link,
}: {
  image: Asset;
  item: string;
  link: Entry<UniversalLinkFields>;
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const hoverAnimation = {
    hover: {
      scale: 1.08,
    },
    initial: {
      scale: 1,
    },
  };

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="w-full"
    >
      <UniversalLink
        link={link}
        key={image.sys.id}
        classes="flex flex-col !items-start md:!items-center md:w-full"
      >
        <div className="rounded-md overflow-hidden w-full md:max-h-[551px]">
          <motion.div
            variants={hoverAnimation}
            initial="initial"
            animate={isHovered ? 'hover' : 'initial'}
            transition={{ duration: 0.3 }}
            className="w-full"
          >
            <LuvImage
              Image={image}
              altText={image.fields.description}
              className="w-full h-full"
            />
          </motion.div>
        </div>
        <p
          className={classNames(
            'text-2xl mt-2 md:text-[32px] md:mt-4 transition-[color] duration-300',
            { 'text-indigo': isHovered },
          )}
        >
          {item}
        </p>
      </UniversalLink>
    </div>
  );
};

const MoreToLove = ({ images, t, CTAs }: ImagesWithCta) => {
  if (!images || !t) return null;

  const { title, firstItem, secondItem } = generateTranslationsObject(
    t.fields as TypeKeyValueListFields,
  ) as homepageRedesignTranslations;

  const items = [firstItem, secondItem];

  return (
    <div className="bg-white">
      <div className="max-w-[1220px] mx-auto px-6 pt-8 pb-10 font-bold md:pt-20 md:pb-24">
        <p className="text-center text-[32px] leading-10 md:text-[42px]">
          {title}
        </p>
        <div className="flex flex-col gap-6 mt-5 md:flex-row md:mt-12 md:justify-center md:gap-5">
          {CTAs &&
            images.map((image, index) => (
              <ImageCard
                key={image.sys.id}
                image={image}
                item={items[index]}
                link={CTAs[index]}
              />
            ))}
        </div>
      </div>
    </div>
  );
};

export default MoreToLove;
